import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	useLazyGetServiceRequestDefinitionForCustomerQuery,
	useSubmitServiceRequestMutation,
	SubmitServiceRequestPayload,
} from "../../../../service/customersupport-service";
import { ServiceRequestType } from "../../../../model/service-request/service-request";
import { SectionDefinition } from "../../../../model/service-request/section-definition";
import { Money } from "../../../../model/common/money";
import { DynamicFieldItem } from "../../../../model/dynamic-forms";
import { EnergyProfile } from "../../../../model/energy-profile";
import { isSectionValid, buildDynamicFieldItem } from "../../components/dynamic-form";
import { useLazyGetEnergyAccountQuery } from "../../../../service/vending-service";
import { buildSubmitPayload } from "./buildSubmitPayload";
import { getTransactionRefFromToken } from "../../../../guards/common/authentification-functions";
import {
	complaintCategoryFrom,
	isIssueTrackerServiceRequest,
	trackReportIssueStarted,
	trackReportIssueSubmitted,
} from "../../../../services/business-analytics/reportIssueEvents";

// Prefills the Meter Number and Customer Address fields from the customer's
// energy account when one exists. Values stay editable; if there's no energy
// account (or no value) the field is left blank for the customer to fill in.
// Fields are matched by their placeholderText ("meterNumber"/"customerAddress").
function buildPrefilledFormValues(
	definition: ServiceRequestType,
	energyProfile: EnergyProfile | undefined
): DynamicFieldItem[][] {
	const perSection = definition.sectionDefinitions.map(() => [] as DynamicFieldItem[]);
	if (!energyProfile) return perSection;

	const valueByPlaceholder: Record<string, string | undefined> = {
		meterNumber: energyProfile.meterNumber || energyProfile.accountNumber || "",
		customerAddress: energyProfile.meterAddress || "",
	};

	definition.sectionDefinitions.forEach((section, index) => {
		(section.attributeDefinitions ?? []).forEach(attribute => {
			const value = attribute.placeholderText ? valueByPlaceholder[attribute.placeholderText] : undefined;
			if (!value) return;
			perSection[index].push(buildDynamicFieldItem({ attribute, fieldValue: value }));
		});
	});

	return perSection;
}

export interface ProcessingState {
	paymentUrl?: string;
	reference: string;
	skipRedirect: boolean;
}

export type SubmitPageState = "loading" | "loaded" | "error";
export type SubmitStep = "dynamicForm" | "confirmation" | "loading" | "success" | "failed";

const TAG_NAME = "New Service";
const CONFIRMATION_TITLE = "Confirmation";
const CONFIRMATION_SUBTITLE = "Review all provided information";

export function useSubmitServiceRequestPage(serviceRequestId: string | undefined) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [fetchDefinition] = useLazyGetServiceRequestDefinitionForCustomerQuery();
	const [fetchEnergyAccount] = useLazyGetEnergyAccountQuery();
	const [submitServiceRequest] = useSubmitServiceRequestMutation();

	const [pageState, setPageState] = useState<SubmitPageState>("loading");
	const [serviceRequest, setServiceRequest] = useState<ServiceRequestType | undefined>(undefined);
	const [currentSection, setCurrentSection] = useState(0);
	const [currentStep, setCurrentStep] = useState<SubmitStep>("dynamicForm");
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [preselectedFormValuesss, setPreselectedFormValuesss] = useState<DynamicFieldItem[][]>([]);

	const [paymentModalOpen, setPaymentModalOpen] = useState(false);
	const [paymentPayload, setPaymentPayload] = useState<SubmitServiceRequestPayload | undefined>(undefined);
	const [processing, setProcessing] = useState<ProcessingState | null>(null);

	const localValuesRef = useRef<DynamicFieldItem[][]>([]);
	// Only the Issue Tracker complaint is a "report issue" for analytics; other service requests are not.
	const isIssueTracker = isIssueTrackerServiceRequest(serviceRequestId);
	// Guards against StrictMode's double effect run, so one opened form is one event.
	const trackedStart = useRef(false);

	// Card payment returns from the payment hub to this route with a ?token=… (a JWT); decode it to the
	// transaction reference and resume verification
	const cardReturnReference = (() => {
		const token = searchParams.get("token");
		if (token) {
			try {
				return getTransactionRefFromToken(token) || null;
			} catch {
				return null;
			}
		}
		return searchParams.get("reference");
	})();

	const sections: SectionDefinition[] = useMemo(
		() => serviceRequest?.sectionDefinitions ?? [],
		[serviceRequest]
	);

	// Price = the request's fixed price or the first entered field carrying a price (mirror getPriceFromChildrenOptions).
	const price = useMemo<Money | undefined>(() => {
		const raw = serviceRequest?.price ?? preselectedFormValuesss.flat().find(item => item.price)?.price;
		return raw ? new Money(raw.currency, raw.amount) : undefined;
	}, [serviceRequest, preselectedFormValuesss]);

	useEffect(() => {
		let active = true;
		(async () => {
			// On return from a card payment, skip the form and go straight to verification.
			if (cardReturnReference) {
				setPageState("loaded");
				setProcessing({ reference: cardReturnReference, skipRedirect: true });
				return;
			}
			if (!serviceRequestId) {
				setPageState("error");
				return;
			}
			setPageState("loading");
			const result = await fetchDefinition(serviceRequestId);
			if (!active) return;
			if (result.data) {
				const definition = result.data;

				// Best-effort prefill from the energy account; a missing account
				// (or error) just leaves the fields empty for the customer.
				let energyProfile: EnergyProfile | undefined;
				try {
					energyProfile = await fetchEnergyAccount().unwrap();
				} catch {
					energyProfile = undefined;
				}
				if (!active) return;

				const perSection = buildPrefilledFormValues(definition, energyProfile);
				localValuesRef.current = perSection.map(arr => [...arr]);
				if (isIssueTracker && !trackedStart.current) {
					trackedStart.current = true;
					trackReportIssueStarted();
				}
				setServiceRequest(definition);
				setPreselectedFormValuesss(perSection);
				setCurrentSection(0);
				setCurrentStep("dynamicForm");
				setIsSubmitted(false);
				setPageState("loaded");
			} else {
				setPageState("error");
			}
		})();
		return () => {
			active = false;
		};
	}, [serviceRequestId, isIssueTracker, fetchDefinition, fetchEnergyAccount, cardReturnReference]);

	const handleValuesChange = useCallback(
		(values: DynamicFieldItem[]) => {
			localValuesRef.current[currentSection] = values;
		},
		[currentSection]
	);

	const goBack = useCallback(() => {
		if (currentStep !== "dynamicForm") {
			setCurrentStep("dynamicForm");
			setIsSubmitted(false);
			return;
		}
		if (currentSection === 0) {
			navigate(-1);
		} else {
			setCurrentSection(s => s - 1);
			setIsSubmitted(false);
		}
	}, [currentStep, currentSection, navigate]);

	const doDirectSubmit = useCallback(
		async (payload: SubmitServiceRequestPayload) => {
			setCurrentStep("loading");
			try {
				await submitServiceRequest(payload).unwrap();
				// The submit response carries no id, so the issue id cannot be reported.
				if (isIssueTracker) trackReportIssueSubmitted(complaintCategoryFrom(serviceRequest, localValuesRef.current));
				setCurrentStep("success");
			} catch {
				setCurrentStep("failed");
			}
		},
		[submitServiceRequest, isIssueTracker, serviceRequest]
	);

	const goNext = useCallback(() => {
		// Commit live values so validation + re-render reflect what was entered.
		const committed = localValuesRef.current.map(arr => [...arr]);
		setPreselectedFormValuesss(committed);
		setIsSubmitted(true);

		if (currentStep === "confirmation") {
			if (!serviceRequest) return;
			const payload = buildSubmitPayload(serviceRequest, committed);
			// Pay only when there is a price AND the request is configured to charge on submit (mirror Flutter).
			if (price && serviceRequest.payOnSubmit === true) {
				setPaymentPayload(payload);
				setPaymentModalOpen(true);
			} else {
				doDirectSubmit(payload);
			}
			return;
		}

		const sectionDef = sections[currentSection];
		const sectionValues = localValuesRef.current[currentSection] ?? [];
		if (sectionDef && !isSectionValid(sectionDef, sectionValues)) {
			return; // block advancing; fields now show required errors
		}

		if (currentSection + 1 < sections.length) {
			setCurrentSection(currentSection + 1);
			setIsSubmitted(false);
		} else {
			setCurrentStep("confirmation");
			setIsSubmitted(false);
		}
	}, [currentStep, currentSection, sections, serviceRequest, price, doDirectSubmit]);

	const closePaymentModal = useCallback(() => setPaymentModalOpen(false), []);

	const handleWalletPaid = useCallback((reference: string) => {
		setPaymentModalOpen(false);
		setProcessing({ reference, skipRedirect: true });
	}, []);

	const handleCardInitiated = useCallback((data: { url: string; reference: string }) => {
		setPaymentModalOpen(false);
		setProcessing({ paymentUrl: data.url, reference: data.reference, skipRedirect: false });
	}, []);

	const handleProcessingSuccess = useCallback(() => {
		setProcessing(null);
		setCurrentStep("success");
	}, []);

	const handleProcessingFailed = useCallback(() => {
		setProcessing(null);
		setCurrentStep("failed");
	}, []);

	const totalSteps = sections.length + 1;
	const activeStep = currentStep === "confirmation" ? sections.length : currentSection;

	const sectionTitle = currentStep === "confirmation" ? CONFIRMATION_TITLE : sections[currentSection]?.name ?? "";
	const headerSubtitle = currentStep === "confirmation" ? CONFIRMATION_SUBTITLE : serviceRequest?.description ?? "";
	const isLastSection = currentSection + 1 >= sections.length;
	const bottomButtonLabel = currentStep === "confirmation" ? "Finish request" : isLastSection ? "Confirmation" : "Next step";

	return {
		pageState,
		serviceRequest,
		currentStep,
		currentSection,
		currentSectionDefinition: sections[currentSection],
		currentSectionValues: preselectedFormValuesss[currentSection] ?? [],
		allSectionValues: preselectedFormValuesss,
		isSubmitted,
		totalSteps,
		activeStep,
		tagName: TAG_NAME,
		sectionTitle,
		headerSubtitle,
		bottomButtonLabel,
		handleValuesChange,
		goBack,
		goNext,
		// payment + submission
		price,
		paymentModalOpen,
		paymentPayload,
		processing,
		closePaymentModal,
		handleWalletPaid,
		handleCardInitiated,
		handleProcessingSuccess,
		handleProcessingFailed,
	};
}
